import { useCallback, useEffect, useRef, useState } from "react";
import { decodeScope, encodeScope } from "@/lib/scope";
import { isScopeAvailable, pickDefaultScope, singlePharmacyScope } from "./scope-sanity";
import type { ScopeRef } from "./types";
import { useScopePersistedState } from "./use-scope-persisted-state";
import { useScopeTree } from "./use-scope-tree";

const DEFAULT_FALLBACK: ScopeRef = { type: "wlasne" };

export interface UseScopeWithTreeOptions {
	persistKey: string;
	/** URL `?scope=` (encoded) — wins over localStorage on first paint. */
	urlScope?: string | null;
	fallback?: ScopeRef;
	/** Sync sanitized / user picks to URL etc. */
	onScopeChange?: (scope: ScopeRef) => void;
}

/**
 * Scope state + persisted key + `/scope/tree` sanity guard.
 * Pages should gate data queries on `scopeReady` so invalid defaults
 * (np. `wlasne` bez aptek wlasnych) nie odpalaja API przed korekta.
 */
export function useScopeWithTree({
	persistKey,
	urlScope,
	fallback = DEFAULT_FALLBACK,
	onScopeChange,
}: UseScopeWithTreeOptions) {
	const [persistedScope, setPersistedScope, hasStoredScope] = useScopePersistedState(
		persistKey,
		fallback,
	);
	const initial: ScopeRef = (urlScope ? decodeScope(urlScope) : null) ?? persistedScope;
	const [scope, setScopeRaw] = useState<ScopeRef>(initial);
	const { data: tree } = useScopeTree();
	// Swiadomy wybor uzytkownika: ?scope= w URL, wpis w localStorage z poprzedniej
	// wizyty albo klikniecie w tej sesji. Autowybor pojedynczej apteki go NIE
	// nadpisuje. Ref bierze wartosc z pierwszego renderu (pozniejszy zapis
	// localStorage pochodzi juz od nas).
	const explicitChoice = useRef(hasStoredScope || Boolean(urlScope && decodeScope(urlScope)));

	const setScope = useCallback(
		(next: ScopeRef) => {
			explicitChoice.current = true;
			setScopeRaw(next);
			setPersistedScope(next);
			onScopeChange?.(next);
		},
		[setPersistedScope, onScopeChange],
	);

	// Konto z dokladnie jedna apteka i bez wlasnego wyboru → ta apteka zamiast
	// rollupu (Wlasne / Franczyza / spolka). `null` przy 0 i >1 aptekach, wiec
	// dotychczasowe zachowanie zostaje nietkniete.
	const autoPick = tree && !explicitChoice.current ? singlePharmacyScope(tree) : null;
	const autoPickId = autoPick?.type === "apteka" ? autoPick.id : null;
	const needsAutoPick =
		autoPickId !== null && !(scope.type === "apteka" && scope.id === autoPickId);

	useEffect(() => {
		if (!tree) return;
		if (!isScopeAvailable(scope, tree)) {
			const safe = pickDefaultScope(tree);
			if (safe) setScope(safe);
			return;
		}
		if (needsAutoPick && autoPickId !== null) setScope({ type: "apteka", id: autoPickId });
	}, [tree, scope, setScope, needsAutoPick, autoPickId]);

	const scopeReady = tree !== undefined && isScopeAvailable(scope, tree) && !needsAutoPick;
	const encodedScope = encodeScope(scope);

	return {
		scope,
		setScope,
		scopeReady,
		encodedScope,
		tree,
	};
}
